import "./index.scss";
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Button from "components/atoms/Button";
import InputSelect from "components/atoms/InputSelect";
import InputDate from "components/atoms/InputDate";
import TableDataComponent from "components/organisms/TableData";
import moment from "moment";
import React, { useEffect, useState } from "react";
import AuditDeletedDataService from "services/AuditDeletedData";
import WilayahService from "services/Wilayah";
import FasyankesService from "services/Fasyankes";
import { Source, MonthOptions } from "utils/constant";

const auditDeletedDataService = new AuditDeletedDataService();
const wilayahService = new WilayahService();
const fasyankesService = new FasyankesService();

const AuditDeletedData = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedSubdistrict, setSelectedSubdistrict] = useState(null);
  const [selectedFasyankes, setSelectedFasyankes] = useState(null);

  const [levelDataOptions, setLevelDataOptions] = useState({
    provinces: [],
    districts: [],
    subdistricts: [],
    fasyankes: [],
  });

  const [dataState, setDataState] = useState({
    page: 1,
    limit: 20,
    data: [],
    total: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });

  useEffect(() => {
    document.title = "SISMAL | Audit Hapus Data";
  }, []);

  // Ambil data provinsi saat pertama kali render
  useEffect(() => {
    fetchProvinces();
  }, []);

  const fetchProvinces = async () => {
    try {
      const response = await wilayahService.getAll(Source.PROVINCE, {
        page: 1,
        limit: 9999,
      });
      setLevelDataOptions((prevState) => ({
        ...prevState,
        provinces: response.province.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDistrictsByProvince = async (provinceId) => {
    try {
      const response = await wilayahService.getAll(Source.DISTRICT, {
        page: 1,
        limit: 9999,
        searchBy: "province_id",
        searchValue: provinceId,
      });
      setLevelDataOptions((prevState) => ({
        ...prevState,
        districts: response.district.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSubdistrictsByDistrict = async (districtId) => {
    try {
      const response = await wilayahService.getAll(Source.SUBDISTRICT, {
        page: 1,
        limit: 9999,
        searchBy: "district_id",
        searchValue: districtId,
      });
      setLevelDataOptions((prevState) => ({
        ...prevState,
        subdistricts: response.subdistrict.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchFasyankesByDistrict = async (districtId) => {
    try {
      const response = await fasyankesService.getAll(Source.FASYANKES, {
        page: 1,
        limit: 9999,
        searchBy: "district_id",
        searchValue: districtId,
      });
      setLevelDataOptions((prevState) => ({
        ...prevState,
        fasyankes: response.fasyankes.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  // Normalisasi nilai dari InputSelect: array kosong / tanpa value dianggap null
  const normalizeSelected = (selected) => {
    if (!selected || Array.isArray(selected) || !selected.value) return null;
    return selected;
  };

  // Cascade: Province berubah → ambil district, reset district/subdistrict/fasyankes
  const handleProvinceChange = (selected) => {
    const value = normalizeSelected(selected);
    setSelectedProvince(value);
    setSelectedDistrict(null);
    setSelectedSubdistrict(null);
    setSelectedFasyankes(null);
    setLevelDataOptions((prevState) => ({
      ...prevState,
      districts: [],
      subdistricts: [],
      fasyankes: [],
    }));

    if (value) {
      const provinceId = value.value.split("|")[0];
      fetchDistrictsByProvince(provinceId);
    }
  };

  // Cascade: District berubah → ambil subdistrict & fasyankes, reset subdistrict/fasyankes
  const handleDistrictChange = (selected) => {
    const value = normalizeSelected(selected);
    setSelectedDistrict(value);
    setSelectedSubdistrict(null);
    setSelectedFasyankes(null);
    setLevelDataOptions((prevState) => ({
      ...prevState,
      subdistricts: [],
      fasyankes: [],
    }));

    if (value) {
      const districtId = value.value.split("|")[0];
      fetchSubdistrictsByDistrict(districtId);
      fetchFasyankesByDistrict(districtId);
    }
  };

  const handleSubdistrictChange = (selected) => {
    setSelectedSubdistrict(normalizeSelected(selected));
  };

  const handleFasyankesChange = (selected) => {
    setSelectedFasyankes(normalizeSelected(selected));
  };

  const fetchData = async (page = dataState.page) => {
    setIsLoading(true);
    try {
      const params = {
        page,
        limit: dataState.limit,
      };

      // Tahun & Bulan → deleted_at_start / deleted_at_end
      if (selectedYear && selectedMonth?.value) {
        const year = selectedYear;
        const month = selectedMonth.value;
        params.deleted_at_start = `${year}-${month}-01`;
        params.deleted_at_end = moment(`${year}-${month}`, "YYYY-MM")
          .endOf("month")
          .format("YYYY-MM-DD");
      }

      // Lokasi
      if (selectedProvince) {
        params.province_id = selectedProvince.value.split("|")[0];
      }
      if (selectedDistrict) {
        params.district_id = selectedDistrict.value.split("|")[0];
      }
      if (selectedSubdistrict) {
        params.subdistrict_id = selectedSubdistrict.value.split("|")[0];
      }
      if (selectedFasyankes) {
        params.fasyankes_origin_id = selectedFasyankes.value.split("|")[0];
      }

      const response = await auditDeletedDataService.getAll(params);

      setDataState((prevState) => ({
        ...prevState,
        data: response?.data || [],
        limit: response?.limit || 20,
        currentPage: response?.page || 1,
        total: response?.total || 0,
        nextPage:
          response?.page * response?.limit < response?.total
            ? response?.page + 1
            : null,
        previousPage: response?.page > 1 ? response?.page - 1 : null,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(dataState.page);
  }, [dataState.page]);

  const onPageChange = (page) => {
    setDataState((prevState) => ({
      ...prevState,
      page,
    }));
  };

  const onApplyFilter = () => {
    setDataState((prevState) => ({
      ...prevState,
      page: 1,
    }));
    fetchData(1);
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    return moment(value).locale("id").format("DD MMMM YYYY HH:mm:ss");
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return moment(value).format("DD/MM/YYYY");
  };

  const formatLocationId = (value) => {
    if (!value) return "-";
    if (value.includes("|")) return value.split("|")[1];
    return value;
  };

  const columns = [
    {
      header: "Waktu Hapus",
      accessorKey: "deleted_at",
      cell: ({ row }) => formatDateTime(row.original.deleted_at),
    },
    {
      header: "Modul",
      accessorKey: "module",
    },
    {
      header: "UUID Data",
      accessorKey: "record_uuid",
    },
    {
      header: "Nama Pasien",
      accessorKey: "patient_name",
      cell: ({ row }) => row.original.patient_name || "-",
    },
    {
      header: "Tgl Kunjungan",
      accessorKey: "patient_visit_date",
      cell: ({ row }) => formatDate(row.original.patient_visit_date),
    },
    {
      header: "Provinsi",
      accessorKey: "province_id",
      cell: ({ row }) => formatLocationId(row.original.province_id),
    },
    {
      header: "Kabupaten",
      accessorKey: "district_id",
      cell: ({ row }) => formatLocationId(row.original.district_id),
    },
    {
      header: "Kecamatan",
      accessorKey: "subdistrict_id",
      cell: ({ row }) => formatLocationId(row.original.subdistrict_id),
    },
    {
      header: "Fasyankes",
      accessorKey: "fasyankes_id",
      cell: ({ row }) => formatLocationId(row.original.fasyankes_id),
    },
    {
      header: "Fasyankes Pencatat",
      accessorKey: "fasyankes_origin_id",
      cell: ({ row }) => formatLocationId(row.original.fasyankes_origin_id),
    },
    {
      header: "User Penghapus",
      accessorKey: "deleted_by_username",
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: dataState?.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="audit-page-container">
      <h1 className="page-title">Audit Hapus Data</h1>

      {/* Filter */}
      <div className="card-filter">
        {/* Baris 1: Tahun & Bulan */}
        <div className="filter-row">
          <div className="filter-col">
            <InputDate
              placeholder="Pilih Tahun"
              name="filter_year"
              dateFormat="YYYY"
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e ? moment(e).format("YYYY") : "");
              }}
              isClearable
            />
          </div>
          <div className="filter-col">
            <InputSelect
              placeholder="Bulan"
              name="filter_month"
              data={MonthOptions}
              value={selectedMonth}
              onChange={(selected) => setSelectedMonth(normalizeSelected(selected))}
              isClearable
            />
          </div>
        </div>

        {/* Baris 2: Provinsi & Kabupaten/Kota */}
        <div className="filter-row">
          <div className="filter-col">
            <InputSelect
              placeholder="Provinsi"
              name="filter_province"
              data={levelDataOptions.provinces}
              value={selectedProvince}
              onChange={handleProvinceChange}
              isClearable
            />
          </div>
          <div className="filter-col">
            <InputSelect
              placeholder="Kabupaten/Kota"
              name="filter_district"
              data={levelDataOptions.districts}
              value={selectedDistrict}
              onChange={handleDistrictChange}
              isClearable
              disabled={!selectedProvince}
            />
          </div>
        </div>

        {/* Baris 3: Kecamatan & Fasyankes */}
        <div className="filter-row">
          <div className="filter-col">
            <InputSelect
              placeholder="Kecamatan"
              name="filter_subdistrict"
              data={levelDataOptions.subdistricts}
              value={selectedSubdistrict}
              onChange={handleSubdistrictChange}
              isClearable
              disabled={!selectedDistrict}
            />
          </div>
          <div className="filter-col">
            <InputSelect
              placeholder="Fasyankes Pencatat"
              name="filter_fasyankes"
              data={levelDataOptions.fasyankes}
              value={selectedFasyankes}
              onChange={handleFasyankesChange}
              isClearable
              disabled={!selectedDistrict}
            />
          </div>
        </div>

        {/* Tombol Terapkan */}
        <div className="filter-action">
          <Button
            variant="primary"
            size="normal"
            onClick={onApplyFilter}
            isDisabled={isLoading || !selectedYear}
          >
            Terapkan
          </Button>
        </div>
      </div>

      {/* Tabel */}
      <div className="table-wrapper">
        <TableDataComponent
          columnLength={columns?.length}
          tableInstance={tableInstance}
          onPageChange={onPageChange}
          loading={isLoading}
          currentPage={dataState?.currentPage}
          limit={dataState?.limit}
          nextPage={dataState?.nextPage}
          previousPage={dataState?.previousPage}
          total={dataState?.total}
        />
      </div>
    </div>
  );
};

export default AuditDeletedData;
